import { Button, Group, Popover, Select, Stack, Text, TextInput } from '@mantine/core';
import { useState } from 'react';
import { initialMessageActions } from '../../helpers/agentUi';
import { actionTag, productFeedTag, questionTag, sanitizeTagText } from '../../helpers/chatBotMessageTags';

const InitialMessageTagBuilder = ({ onInsert, productFeeds, agentUi, locale }) => {
  const [questionOpen, setQuestionOpen] = useState(false);
  const [feedOpen, setFeedOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [visual, setVisual] = useState('');
  const [prompt, setPrompt] = useState('');
  const [feedId, setFeedId] = useState(null);
  const [feedTitle, setFeedTitle] = useState('');

  const feedOptions = (productFeeds || []).reduce((options, feed) => {
    const value = String(feed.productFeedId);
    if (feed.productFeedId && !options.some((option) => option.value === value)) {
      options.push({ value, label: feed.name });
    }
    return options;
  }, []);

  // Only actions that run without tag parameters; none set (agentUi null) hides the button.
  const actionOptions = initialMessageActions(agentUi).map((action) => {
    const label = action.labels?.[(locale || '').slice(0, 2)]?.text ?? Object.values(action.labels || {})[0]?.text;
    return { value: action.type, label: label ? `${action.type} · ${label}` : action.type };
  });

  const insertAction = () => {
    onInsert(actionTag(actionType));
    setActionType(null);
    setActionOpen(false);
  };

  const insertQuestion = () => {
    onInsert(questionTag(visual, prompt));
    setVisual('');
    setPrompt('');
    setQuestionOpen(false);
  };

  const insertFeed = () => {
    onInsert(productFeedTag(feedId, feedTitle));
    setFeedId(null);
    setFeedTitle('');
    setFeedOpen(false);
  };

  return (
    <Group gap="xs" mt={4}>
      <Popover opened={questionOpen} onChange={setQuestionOpen} position="bottom-start" withArrow shadow="md">
        <Popover.Target>
          <Button variant="default" size="xs" onClick={() => setQuestionOpen((open) => !open)}>
            Add question
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="xs" w={280}>
            <TextInput
              size="xs"
              placeholder="Button label"
              value={visual}
              onChange={(e) => setVisual(e.target.value)}
            />
            <TextInput
              size="xs"
              placeholder="Message it sends"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <Button size="xs" onClick={insertQuestion} disabled={!sanitizeTagText(visual) || !sanitizeTagText(prompt)}>
              Insert
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>

      <Popover opened={feedOpen} onChange={setFeedOpen} position="bottom-start" withArrow shadow="md">
        <Popover.Target>
          <Button
            variant="default"
            size="xs"
            disabled={feedOptions.length === 0}
            onClick={() => setFeedOpen((open) => !open)}
          >
            Add product feed
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="xs" w={280}>
            <Select
              size="xs"
              data={feedOptions}
              value={feedId}
              onChange={setFeedId}
              placeholder="Select a product feed"
            />
            <TextInput
              size="xs"
              placeholder="Carousel title (optional)"
              value={feedTitle}
              onChange={(e) => setFeedTitle(e.target.value)}
            />
            <Button size="xs" onClick={insertFeed} disabled={!feedId}>
              Insert
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>

      {actionOptions.length > 0 && (
        <Popover opened={actionOpen} onChange={setActionOpen} position="bottom-start" withArrow shadow="md">
          <Popover.Target>
            <Button variant="default" size="xs" onClick={() => setActionOpen((open) => !open)}>
              Add action
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap="xs" w={280}>
              <Select
                size="xs"
                data={actionOptions}
                value={actionType}
                onChange={setActionType}
                placeholder="Select an action"
                aria-label="Action"
                description="A site function must run without parameters"
              />
              <Button size="xs" onClick={insertAction} disabled={!actionType}>
                Insert
              </Button>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      )}

      {feedOptions.length === 0 && (
        <Text size="xs" c="dimmed">
          Product feeds are set up by Releva — contact support to enable them.
        </Text>
      )}
    </Group>
  );
};

export default InitialMessageTagBuilder;
